import { useModalQueue } from '@carvana/merch-react-hooks';
import { createContext, useCallback, useMemo, useState } from 'react';
import MoreDetailsModalSections from '~/constants/MoreDetailsModalSections';
import { AppliedFilters } from '~/contracts';
import { ModalId, ModalPriorities } from './constants';

type MoreDetailsOptions = {
  openTo: MoreDetailsModalSections;
  vehicleId?: number;
};

type ContextType = {
  filtersToSave: AppliedFilters;
  isActivationModalOpen: boolean;
  isAnyModalOpen: boolean;
  isCoApplicantModalOpen: boolean;
  isCreateSavedSearchModalOpen: boolean;
  isEVTaxCreditModalOpen: boolean;
  isFilterModalOpen: boolean;
  isGeolocationModalOpen: boolean;
  isManageSavedSearchesModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  isWipNotificationModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  setActivationModalOpen: (isOpen: boolean) => void;
  setCoApplicantModalOpen: (isOpen: boolean) => void;
  setCreateSavedSearchOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setEVTaxCreditModalOpen: (isOpen: boolean) => void;
  setFilterModalOpen: (isOpen: boolean) => void;
  setGeolocationModalOpen: (isOpen: boolean) => void;
  setManageSavedSearchesOpen: (isOpen: boolean) => void;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
  setIsSearchBarFocused: (isFocused: boolean) => void;
  setSearchBarModalOpen: (isOpen: boolean) => void;
  setSoftPullModalOpen: (isOpen: boolean) => void;
  setWipNotificationModalOpen: (isOpen: boolean) => void;
};

export const defaultContext: ContextType = {
  filtersToSave: {},
  isActivationModalOpen: false,
  isAnyModalOpen: false,
  isCoApplicantModalOpen: false,
  isCreateSavedSearchModalOpen: false,
  isEVTaxCreditModalOpen: false,
  isFilterModalOpen: false,
  isGeolocationModalOpen: false,
  isManageSavedSearchesModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isSoftPullModalOpen: false,
  isWipNotificationModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails,
    vehicleId: undefined
  },
  setActivationModalOpen() {},
  setCoApplicantModalOpen() {},
  setCreateSavedSearchOpen() {},
  setEVTaxCreditModalOpen() {},
  setFilterModalOpen() {},
  setGeolocationModalOpen() {},
  setManageSavedSearchesOpen() {},
  setMoreDetailsModalOpen() {},
  setIsSearchBarFocused() {},
  setSearchBarModalOpen() {},
  setSoftPullModalOpen() {},
  setWipNotificationModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }) {
  const [currentModalId, generateOpener] = useModalQueue(ModalPriorities);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);

  const {
    setActivationModalOpen,
    setCoApplicantModalOpen,
    setCreateSavedSearchOpen,
    setEVTaxCreditModalOpen,
    setGeolocationModalOpen,
    setManageSavedSearchesOpen,
    setMoreDetailsModalOpen,
    setSearchBarModalOpen,
    setSoftPullModalOpen,
    setWipNotificationModalOpen
  } = useMemo(
    () => ({
      setActivationModalOpen: generateOpener(ModalId.Activation),
      setCoApplicantModalOpen: generateOpener(ModalId.CoApplicant),
      setCreateSavedSearchOpen: generateOpener(ModalId.CreateSavedSearch),
      setEVTaxCreditModalOpen: generateOpener(ModalId.EVTaxCredit),
      setGeolocationModalOpen: generateOpener(ModalId.Geolocation),
      setManageSavedSearchesOpen: generateOpener(ModalId.ManageSavedSearches),
      setMoreDetailsModalOpen: generateOpener(ModalId.MoreDetails),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setWipNotificationModalOpen: generateOpener(ModalId.WIPNotifications)
    }),
    [generateOpener]
  );

  const setCreateSavedSearchOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: Partial<AppliedFilters>) => {
      setFiltersToSave(filters);
      setCreateSavedSearchOpen(isOpen);
    },
    [setCreateSavedSearchOpen]
  );

  const setMoreDetailsModalOpenWithVehicle = useCallback<
    ContextType['setMoreDetailsModalOpen']
  >(
    (isOpen: boolean, options?: MoreDetailsOptions) => {
      if (options) setMoreDetailsOptions(options);
      setMoreDetailsModalOpen(isOpen);
    },
    [setMoreDetailsModalOpen]
  );

  const value = useMemo<ContextType>(
    (): ContextType => ({
      filtersToSave: filtersToSave,
      isActivationModalOpen: currentModalId === ModalId.Activation,
      isAnyModalOpen: currentModalId > -1 || isFilterModalOpen,
      isCoApplicantModalOpen: currentModalId === ModalId.CoApplicant,
      isCreateSavedSearchModalOpen:
        currentModalId === ModalId.CreateSavedSearch,
      isEVTaxCreditModalOpen: currentModalId === ModalId.EVTaxCredit,
      isFilterModalOpen,
      isGeolocationModalOpen: currentModalId === ModalId.Geolocation,
      isManageSavedSearchesModalOpen:
        currentModalId === ModalId.ManageSavedSearches,
      isMoreDetailsModalOpen: currentModalId === ModalId.MoreDetails,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isWipNotificationModalOpen: currentModalId === ModalId.WIPNotifications,
      moreDetailsOptions,
      setActivationModalOpen,
      setCoApplicantModalOpen,
      setCreateSavedSearchOpen: setCreateSavedSearchOpenWithFiltersToSave,
      setEVTaxCreditModalOpen,
      setFilterModalOpen,
      setIsSearchBarFocused,
      setGeolocationModalOpen,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithVehicle,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setWipNotificationModalOpen
    }),
    [
      filtersToSave,
      currentModalId,
      isFilterModalOpen,
      isSearchBarFocused,
      moreDetailsOptions,
      setActivationModalOpen,
      setCoApplicantModalOpen,
      setCreateSavedSearchOpenWithFiltersToSave,
      setEVTaxCreditModalOpen,
      setGeolocationModalOpen,
      setManageSavedSearchesOpen,
      setMoreDetailsModalOpenWithVehicle,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setWipNotificationModalOpen
    ]
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export default ModalContext;
